import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Edit2, Truck, Phone, Mail, MapPin, Trash2, Package, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VendorFormDialog from "./components/VendorFormDialog";

export default function VendorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/spares/vendors/${id}`);
      setVendor(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load vendor");
      nav("/spare-parts/vendors");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const deactivate = async () => {
    if (!window.confirm("Deactivate this vendor? Parts linked to them will remain visible.")) return;
    try {
      await api.delete(`/spares/vendors/${id}`);
      toast.success("Vendor deactivated");
      nav("/spare-parts/vendors");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not deactivate"); }
  };

  if (loading || !vendor) {
    return <div className="py-10 text-center text-[13px] text-[#667085]">Loading vendor…</div>;
  }
  const partsSupplied = vendor.parts_supplied || [];

  return (
    <div className="space-y-4" data-testid="spare-vendor-detail-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Link to="/spare-parts/vendors" data-testid="vendor-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">{vendor.name}</h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${
                vendor.active ? "bg-[#12B76A]/12 text-[#0F9256]" : "bg-[#98A2B3]/20 text-[#475467]"
              }`}>
                {vendor.active ? "Active" : "Inactive"}
              </span>
            </div>
            {vendor.contact_person && (
              <div className="text-[12.5px] text-[#667085] mt-0.5">Attn: {vendor.contact_person}</div>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEditOpen(true)}
                  data-testid="vendor-detail-edit"
                  className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
            <Edit2 size={13} /> Edit
          </button>
          {vendor.active && (
            <button onClick={deactivate}
                    data-testid="vendor-detail-deactivate"
                    className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#e63946] hover:bg-[#e63946]/10 inline-flex items-center gap-1.5">
              <Trash2 size={13} /> Deactivate
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <section className="lg:col-span-2 bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-3" data-testid="vendor-detail-contact">
          <h3 className="text-[13px] font-bold text-[#1d3557]">Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
            {vendor.phone && (
              <div className="flex items-center gap-2">
                <Phone size={12} className="text-[#457b9d]" />
                <a href={`tel:${vendor.phone}`} className="text-[#1d3557] hover:text-[#e63946] font-mono">{vendor.phone}</a>
              </div>
            )}
            {vendor.whatsapp && (
              <div className="flex items-center gap-2">
                <Phone size={12} className="text-[#12B76A]" />
                <a href={`https://wa.me/${vendor.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                   className="text-[#1d3557] hover:text-[#e63946] font-mono">
                  {vendor.whatsapp}
                </a>
              </div>
            )}
            {vendor.email && (
              <div className="flex items-center gap-2">
                <Mail size={12} className="text-[#457b9d]" />
                <a href={`mailto:${vendor.email}`} className="text-[#1d3557] hover:text-[#e63946]">{vendor.email}</a>
              </div>
            )}
            {vendor.gstin && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">GSTIN</span>
                <span className="font-mono">{vendor.gstin}</span>
              </div>
            )}
          </div>
          {(vendor.address || vendor.city || vendor.state) && (
            <div className="flex items-start gap-2 text-[12.5px] text-[#1d3557] border-t border-[#F1FAEE] pt-2">
              <MapPin size={12} className="text-[#457b9d] mt-1" />
              <div>
                {vendor.address && <div>{vendor.address}</div>}
                <div className="text-[#667085]">{[vendor.city, vendor.state, vendor.pin].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          )}
          {vendor.payment_terms && (
            <div className="text-[12.5px]">
              <div className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">Payment terms</div>
              <div className="text-[#1d3557]">{vendor.payment_terms}</div>
            </div>
          )}
          {vendor.notes && (
            <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/30 px-3 py-2 text-[12px] text-[#1d3557]">
              <div className="font-bold text-[10.5px] uppercase tracking-wider text-[#457b9d] mb-1">Notes</div>
              {vendor.notes}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="vendor-detail-parts">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-md bg-[#a8dadc]/30 grid place-items-center text-[#457b9d]">
              <Package size={14} />
            </div>
            <h3 className="text-[13px] font-bold text-[#1d3557]">Parts supplied ({partsSupplied.length})</h3>
          </div>
          {partsSupplied.length === 0 ? (
            <div className="text-[12px] text-[#667085] py-4">
              No parts are pointing at this vendor. Set them as the preferred vendor from any part detail page.
            </div>
          ) : (
            <ul className="divide-y divide-[#F1FAEE] max-h-72 overflow-y-auto">
              {partsSupplied.map((p) => (
                <li key={p.id}>
                  <Link to={`/spare-parts/stock/${p.id}`}
                        className="py-2.5 flex items-center gap-2 hover:bg-[#F7F9FC] px-1 rounded"
                        data-testid={`vendor-part-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1d3557] truncate">{p.name}</div>
                      {p.part_number && <div className="text-[11px] font-mono text-[#667085]">{p.part_number}</div>}
                    </div>
                    <ChevronRight size={13} className="text-[#a8dadc]" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <VendorFormDialog
        open={editOpen} initial={vendor}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />
    </div>
  );
}
